import { useState } from "react";

import { api } from "@/api/client";
import { useStore } from "@/state/store";
import { Icon } from "@/components/atoms/Icons";
import { Segmented } from "@/components/atoms/Segmented";

type Asset = "USDC" | "USDT";
type Network = "polygon" | "tron";

export function WithdrawModal() {
  const open = useStore((s) => s.withdrawOpen);
  const setOpen = useStore((s) => s.setWithdrawOpen);
  const userId = useStore((s) => s.userId);
  const showToast = useStore((s) => s.showToast);
  const [asset, setAsset] = useState<Asset>("USDC");
  const [network, setNetwork] = useState<Network>("polygon");
  const [destination, setDestination] = useState("");
  const [amount, setAmount] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const submit = async () => {
    if (!userId || !destination || !amount) return;
    setErr(null);
    setSubmitting(true);
    const { data, error } = await api.POST("/v1/wallet/withdrawals", {
      body: {
        asset,
        network,
        destination_address: destination,
        amount,
        idempotency_key: `withdraw-${Date.now()}`,
      },
    });
    setSubmitting(false);
    if (error) {
      setErr(extractMsg(error) ?? "Withdrawal rejected");
      return;
    }
    if (data) {
      showToast({
        kind: "success",
        message: `Withdrawal queued (#${data.id.slice(0, 6)})`,
      });
      setOpen(false);
      setDestination("");
      setAmount("");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div className="w-[460px] max-w-[92vw] rounded-modal border border-line bg-bg-1 p-6 shadow-[var(--shadow-card)]">
        <div className="flex items-center justify-between">
          <h2 className="text-[18px] font-semibold">Withdraw crypto</h2>
          <button
            className="btn-ghost h-9 w-9 rounded-full p-0"
            onClick={() => setOpen(false)}
            aria-label="Close"
          >
            <Icon.X size={16} />
          </button>
        </div>

        <div className="mt-4 flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-2">
            <Segmented<Asset>
              options={[
                { value: "USDC", label: "USDC" },
                { value: "USDT", label: "USDT" },
              ]}
              value={asset}
              onChange={setAsset}
            />
            <Segmented<Network>
              options={[
                { value: "polygon", label: "Polygon" },
                { value: "tron", label: "Tron" },
              ]}
              value={network}
              onChange={setNetwork}
            />
          </div>

          <label className="block">
            <span className="mb-1 block text-[11px] uppercase tracking-wider text-muted">
              Destination address
            </span>
            <input
              className="input font-mono"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              placeholder="0x…"
              spellCheck={false}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] uppercase tracking-wider text-muted">
              Amount ({asset})
            </span>
            <input
              className="input font-mono"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="decimal"
              placeholder="0.00"
            />
          </label>

          {err && (
            <div className="rounded-[10px] border border-no/40 bg-no/10 px-3 py-2 text-[12.5px] text-no">
              {err}
            </div>
          )}

          <div className="rounded-[10px] border border-warn/40 bg-warn/10 px-3 py-2 text-[11.5px] text-warn">
            First-time destinations are time-locked for 24h. You can cancel from the activity list.
          </div>

          <button
            className="btn btn-primary w-full !py-3"
            disabled={submitting || !destination || !amount}
            onClick={submit}
          >
            {submitting ? "…" : "Request withdrawal"}
          </button>
        </div>
      </div>
    </div>
  );
}

function extractMsg(err: unknown): string | null {
  if (typeof err === "object" && err && "message" in err) {
    return String((err as { message: unknown }).message);
  }
  return null;
}
